// PWA helpers — service worker registration + push subscription utilities.
//
// PR 4a installs this module and wires the registration. PR 4b builds on
// the helpers below (subscribe, unsubscribe, getExistingSubscription) for
// the opt-in banner flow.
//
// The service worker is registered with `scope: '/'` so it can receive
// pushes for any URL on the origin, but we only register from the crew
// portal route — there's no current call site for push from the
// estimator/CRM pages, and registering everywhere would trigger the
// install/activate lifecycle on every dashboard refresh.

let registrationPromise = null;

export function isServiceWorkerSupported() {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

export function isPushSupported() {
  return isServiceWorkerSupported() && typeof window !== 'undefined' && 'PushManager' in window;
}

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  // iOS Safari: navigator.standalone. Everyone else: matchMedia.
  return (
    window.navigator.standalone === true ||
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
  );
}

export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  // iPadOS reports as Mac in modern Safari; check for touch as a tiebreaker.
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && navigator.maxTouchPoints > 1);
}

// Registers /sw.js once per page load. Subsequent calls reuse the
// in-flight promise so callers don't have to coordinate.
//
// SW update flow (added to fix the iOS PWA stale-SW problem):
//   1. register() returns the existing registration (or creates one).
//   2. reg.update() forces a fresh fetch of /sw.js — iOS PWAs otherwise
//      cache the SW for up to 24 hours, which means a deployed change
//      to the push-event handler can sit unused for a full day.
//   3. We listen for `updatefound`: when a new SW reaches the `installed`
//      state with the page already controlled by an old SW, that new SW
//      sits in "waiting" until all tabs close. We post a SKIP_WAITING
//      message so it activates right away (sw.js implements the handler).
//   4. `controllerchange` fires once the new SW takes over — we do NOT
//      reload (that would interrupt the lead), but we log it so we know
//      the update completed without the user noticing.
//
// Effect: deploying an sw.js change becomes visible on next PWA launch
// instead of "sometime in the next 24h on Apple's whim."
export function registerServiceWorker() {
  if (!isServiceWorkerSupported()) return Promise.resolve(null);
  if (registrationPromise) return registrationPromise;

  registrationPromise = navigator.serviceWorker
    .register('/sw.js', { scope: '/' })
    .then((reg) => {
      // Force the update check the spec gates behind a 24-hour timer.
      // Safe to fail (offline, network blip) — we just won't have the
      // newest SW yet, which matches current behavior.
      try { reg.update().catch(() => {}); } catch (_) {}

      // If a new SW is found during registration or any later update()
      // call, listen for the install transition and skipWaiting once
      // installed (only when an old SW is currently in control — first
      // installs activate on their own via the SW's own install handler).
      reg.addEventListener('updatefound', () => {
        const incoming = reg.installing;
        if (!incoming) return;
        incoming.addEventListener('statechange', () => {
          if (incoming.state === 'installed' && navigator.serviceWorker.controller) {
            try { incoming.postMessage({ type: 'SKIP_WAITING' }); } catch (_) {}
          }
        });
      });

      // Best-effort log — surfaces in Safari/Chrome devtools when
      // attached. iOS PWAs can't be attached to easily, but the next
      // push event from the new SW is the real signal that this worked.
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('[pwa] new service worker took control');
      });

      return reg;
    })
    .catch((err) => {
      // Don't blow up the page if registration fails — push is a
      // nice-to-have. Log and return null so callers can branch.
      console.warn('[pwa] service worker registration failed:', err);
      registrationPromise = null;
      return null;
    });

  return registrationPromise;
}

// Web push uses VAPID's URL-safe base64 encoding. The browser wants a
// Uint8Array, so we decode here at subscribe time.
export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = typeof atob === 'function' ? atob(base64) : Buffer.from(base64, 'base64').toString('binary');
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function getExistingSubscription() {
  if (!isPushSupported()) return null;
  const reg = await registerServiceWorker();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

// Subscribe the active service worker to push using the VAPID public key.
// Caller is responsible for POSTing the resulting subscription to the
// server endpoint. Throws on failure so the caller can show a toast.
export async function subscribeToPush(vapidPublicKey) {
  if (!isPushSupported()) throw new Error('Push notifications not supported in this browser');
  if (!vapidPublicKey) throw new Error('Missing VAPID public key');

  const reg = await registerServiceWorker();
  if (!reg) throw new Error('Service worker not registered');

  const existing = await reg.pushManager.getSubscription();
  if (existing) return existing;

  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });
}

export async function unsubscribeFromPush() {
  const sub = await getExistingSubscription();
  if (!sub) return false;
  return sub.unsubscribe();
}
